"use client";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import {
  ApiError,
  getApiErrorMessage,
  isTerminalApiStatus,
} from "@/lib/auth/apiError";
import { showToast } from "@/components/utils/toast-utils";

/*
 * Most callers read only `data`, so a query that fails — a schema that no
 * longer matches the wire, a 500 the route answered as an empty page — renders
 * as a blank list or a 404 and leaves nothing behind. Logging at the cache
 * makes every failure name itself in the console with its query key, so the
 * next boundary mismatch is read off the screen instead of traced backwards
 * from an empty page.
 */
const queryCache = new QueryCache({
  onError: (error, query) => {
    console.error("[query failed]", query.queryKey, error.message);
  },
});

const mutationCache = new MutationCache({
  onError: (error, _variables, _context, mutation) => {
    console.error(
      "[mutation failed]",
      mutation.options.mutationKey,
      error.message,
    );

    if (!mutation.options.onError) {
      showToast({
        title: "Действие не выполнено",
        description:
          error instanceof ApiError
            ? getApiErrorMessage(error, error.message)
            : "Не удалось выполнить действие. Попробуйте позже.",
        type: "error",
      });
    }
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 минут
      gcTime: 10 * 60 * 1000,
      retry: (failureCount, error) =>
        !(error instanceof ApiError && isTerminalApiStatus(error.status)) &&
        failureCount < 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

export default function Providers({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
